import React from "react";
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import ShiftITLogo from "../images/ShiftITLogo.png";
import {
  Page,
  HeaderBar,
  HeaderInner,
  LogoLink,
  LogoImg,
  Main,
  Article,
  ArticleHeader,
  Title,
  Content,
} from "../components/legal/LegalElements";

// Simple legal page layout (dark theme + readable content width)
export default function PrivacyPolicyPage() {
  return (
    <Page>
      <SEO
        title="Privacy Policy | Shift IT"
        description="Read the Shift IT Privacy Policy to understand how we collect, use, and safeguard information across our services and assessment tools."
        canonical="https://shift-it.io/privacy"
      />
      <HeaderBar>
        <HeaderInner>
          <LogoLink href="/" aria-label="Go to home">
            <LogoImg src={ShiftITLogo} alt="Shift IT Logo" />
          </LogoLink>
        </HeaderInner>
      </HeaderBar>
      <Main>
        <Article>
          <ArticleHeader>
            <Title>Privacy Policy</Title>
          </ArticleHeader>
          <Content>
            <p>
              <strong>Updated: 26 August 2025</strong>
            </p>
            <h2>1) Who we are and what this covers</h2>
            <p>
              This Products and Services Privacy Policy ("Policy") describes how
              Shift IT, LLC and our affiliates ("Shift IT," "we," "us," "our")
              collect, use, share, and store information in connection with:
            </p>
            <ul>
              <li>
                Our managed and professional services (e.g., Microsoft 365
                identity &amp; email, device management/Intune, Teams/SharePoint
                migrations, automation &amp; integrations, monitoring/remote
                support, and related consulting), and
              </li>
              <li>
                Our websites and client portals (collectively, the "Services").
              </li>
            </ul>
            <p>
              We do not sell or rent your personal information. We use data to
              deliver, secure, and improve the Services.
            </p>
            <p>
              <strong>Controller vs. processor</strong>
            </p>
            <ul>
              <li>
                For our own site, billing, account management, and marketing,
                Shift IT acts as a data controller.
              </li>
              <li>
                For client environments (e.g., managing Microsoft 365 tenants,
                devices, networks, and connected tools), Shift IT generally acts
                as a data processor under the client's instructions and
                contract. End-users of our clients should send privacy requests
                to their employer/organization (the controller). We assist the
                controller in fulfilling those requests.
              </li>
            </ul>
            <h2>2) Personal information we collect</h2>
            <p>
              Account &amp; registration. Name, company, role, email, phone,
              billing and payment details, and the names/emails of authorized
              users on your account.
            </p>
            <p>
              Support &amp; communications. Information you submit via forms,
              emails, tickets, chat, surveys, and recordings/notes necessary to
              provide support or training.
            </p>
            <p>
              Events. Name and contact details if you register for Shift IT
              events or webinars.
            </p>
            <p>
              Website/portals (cookies &amp; similar tech). We and our service
              providers may use cookies, pixels, and analytics to understand
              site usage and tailor communications. See "Marketing &amp;
              cookies" and "Your choices."
            </p>
            <p>
              Geographic/time data. Zip or area code, time zone, and similar
              coarse location signals to configure services and troubleshoot.
            </p>
            <p>
              Device, app, and network telemetry (managed clients). Depending on
              your plan and tooling, we may process:
            </p>
            <ul>
              <li>
                Device and asset metadata (e.g., device name, model, serial, OS
                version, MAC/IP addresses, installed agents, configuration
                state).
              </li>
              <li>
                Security/compliance state (e.g., disk encryption status, patch
                status, AV/XDR signals).
              </li>
              <li>
                Event and diagnostic logs (e.g., uptime, disconnections, error
                codes, policy results).
              </li>
              <li>
                Limited traffic counters (e.g., volume per
                application/endpoint), typically without content inspection.
              </li>
              <li>
                Remote-session metadata (timestamps, technician identity,
                actions performed). We do not access a device without
                authorization from the client or designated contact.
              </li>
            </ul>
            <p>
              Environmental signals (if present). If your solution includes
              sensors, we may process readings (e.g., temperature, motion) to
              deliver alerts/automations.
            </p>
            <p>
              Third-party sources. We may receive information from partners
              (e.g., Microsoft, distributors, monitoring vendors, payment
              processors), or from tools you connect to the Services.
            </p>
            <h2>3) How we use information</h2>
            <ul>
              <li>
                Provide &amp; operate the Services (setup, migrations, device
                management, monitoring, remote support, backups, training).
              </li>
              <li>
                Secure systems and detect, investigate, and mitigate abuse or
                threats.
              </li>
              <li>
                Support &amp; communicate, including service notices and product
                updates.
              </li>
              <li>
                Improve &amp; develop, including troubleshooting, analytics, and
                quality assurance.
              </li>
              <li>Billing &amp; account administration.</li>
              <li>
                Marketing about Shift IT products/services (you can opt out at
                any time).
              </li>
              <li>
                Aggregate/de-identify data for statistics, capacity planning,
                and benchmarking.
              </li>
            </ul>
            <h2>4) Sharing information</h2>
            <ul>
              <li>
                Service providers (subprocessors). We use vetted vendors under
                contract to host, process, or support the Services (examples may
                include Microsoft, Pax8, NinjaOne, Domotz, helpdesk/CRM, billing
                and payment processors). They may access data only to perform
                their tasks for us and must protect it.
              </li>
              <li>
                Partners/integrations. At your direction, we connect to
                third-party tools. Data shared with those tools is governed by
                those third parties' privacy policies while in their systems.
              </li>
              <li>
                Business transfers. Information may be transferred in connection
                with mergers, acquisitions, or asset sales.
              </li>
              <li>
                Legal &amp; safety. We may disclose information to comply with
                law, respond to lawful requests, enforce agreements, or protect
                rights, property, and safety.
              </li>
            </ul>
            <h2>5) International processing &amp; data location</h2>
            <p>
              We primarily process North American client data in the United
              States. We may process or access data from other locations where
              our personnel or vendors operate. When we transfer data, we use
              appropriate safeguards consistent with this Policy and applicable
              law.
            </p>
            <h2>6) Retention &amp; deletion</h2>
            <p>
              We retain personal information for as long as necessary to provide
              the Services, comply with legal obligations, resolve disputes, and
              enforce agreements. Typical examples:
            </p>
            <ul>
              <li>Support tickets &amp; logs: ~3 years.</li>
              <li>
                Device/telemetry data: ~12-24 months, unless needed for security
                investigations.
              </li>
              <li>Billing records: As required by tax and accounting laws.</li>
            </ul>
            <p>
              Clients may request deletion for controller-managed data.
              De-identified/aggregated data may be retained indefinitely.
            </p>
            <h2>7) Security</h2>
            <p>
              We use administrative, technical, and physical safeguards
              appropriate to the risk, including encryption in transit, hardened
              authentication, access controls, logging, and vulnerability/patch
              management. No system is perfectly secure; we cannot guarantee
              absolute security.
            </p>
            <h2>8) Your privacy rights</h2>
            <p>Your rights vary by jurisdiction and by our role:</p>
            <p>
              When we are the processor (most managed services): Please contact
              your organization (the controller). We will assist them in
              responding to requests (access, correction, deletion, portability,
              objection/restriction) within required timelines.
            </p>
            <p>
              When we are the controller (our website, billing, marketing): You
              may request access, correction, deletion, or portability, and
              object to/limit processing of your information. See "Contact us."
            </p>
            <p>
              U.S. state notices (e.g., California): If you are a California
              resident, you may have additional rights to know/access, correct,
              delete, and to opt out of "sale" or "sharing" for cross-context
              behavioral advertising. Shift IT does not sell personal
              information, and we honor applicable opt-out signals for
              cross-context ads where relevant.
            </p>
            <h2>9) Marketing &amp; cookies --- your choices</h2>
            <p>
              You can unsubscribe from non-transactional emails via the link in
              the message or by contacting us. You can manage cookies through
              your browser settings. Where required, we present a cookie notice
              and respect your preferences.
            </p>
            <h2>10) Children</h2>
            <p>
              The Services are not directed to children under 18, and we do not
              knowingly collect their personal information.
            </p>
            <h2>11) Changes to this Policy</h2>
            <p>
              We may update this Policy from time to time. Material changes will
              be highlighted, and when required, we will seek consent. The
              "Updated" date shows the latest revision.
            </p>
            <h2>12) Contact us</h2>
            <p>Email: support@shift-it.io</p>
            <p>
              For security/privacy inquiries, please use the subject line
              "Privacy Request" and include the context (controller vs.
              processor).
            </p>
            <h2>13) Compliance &amp; exceptions</h2>
            <p>
              Compliance with this Policy and related security standards is
              expected. If a documented exception is needed for technical or
              business reasons, submit a request to Shift IT's Security Lead for
              review and approval.
            </p>
            <h2>14) Sub-processors (overview)</h2>
            <p>
              We maintain a current list of core sub-processors used for
              hosting, monitoring, ticketing, and billing and will provide it
              upon request.
            </p>
            <h2>15) Revision history</h2>
            <ul>
              <li>Aug 26, 2025 --- Initial draft (Reviewer: Sean P. Cook)</li>
            </ul>
          </Content>
        </Article>
      </Main>
      <Footer />
    </Page>
  );
}
